import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { Button, Sheet, useToast } from '../ui';
import { useAppDispatch } from '../../store/hooks';
import { upgradeGuest } from '../../store/slices/authSlice';
import { useTranslation } from '../../i18n';
import { translateAuthError } from '../../i18n/authErrors';

export interface SaveAccountSheetProps {
  open: boolean;
  onClose: () => void;
}

const FIELD =
  'hc-input min-h-[48px] border border-white/15 focus:outline-none focus:ring-2 focus:ring-hc-accent';

/** The youngest birth date the form accepts (18 years ago), as `YYYY-MM-DD`. */
export function latestAdultBirthDate(now: Date = new Date()): string {
  const limit = new Date(now);
  limit.setFullYear(limit.getFullYear() - 18);
  return limit.toISOString().split('T')[0];
}

function SaveAccountForm({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const mismatch = confirmation !== '' && confirmation !== password;
  const canSubmit =
    !busy &&
    email.trim() !== '' &&
    password !== '' &&
    confirmation === password &&
    birthDate !== '' &&
    accepted;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError('');
    setFieldErrors({});
    const result = await dispatch(
      upgradeGuest({
        email: email.trim(),
        password,
        password_confirmation: confirmation,
        birth_date: birthDate,
        adult_terms_accepted: accepted,
      })
    );
    if (upgradeGuest.fulfilled.match(result)) {
      onClose();
      showToast({ message: t('guest.save.done') });
      return;
    }
    const failure = result.payload;
    setFieldErrors(failure?.fields ?? {});
    setError(
      failure && Object.keys(failure.fields).length === 0
        ? translateAuthError(t, failure.message)
        : ''
    );
    setBusy(false);
  };

  return (
    <form
      onSubmit={event => void submit(event)}
      className="flex flex-col gap-4 pb-2"
      noValidate
    >
      <p className="text-sm text-white/70">{t('guest.save.intro')}</p>

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="save-email"
          className="text-sm font-medium text-white/70"
        >
          {t('auth.field.email')}
        </label>
        <input
          id="save-email"
          type="email"
          dir="ltr"
          autoComplete="email"
          required
          value={email}
          onChange={event => setEmail(event.target.value)}
          placeholder={t('auth.placeholder.email')}
          aria-invalid={fieldErrors.email ? true : undefined}
          aria-describedby={fieldErrors.email ? 'save-email-error' : undefined}
          className={FIELD}
        />
        {fieldErrors.email ? (
          <p id="save-email-error" className="text-sm text-red-300">
            {fieldErrors.email}
          </p>
        ) : null}
      </div>

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="save-password"
          className="text-sm font-medium text-white/70"
        >
          {t('auth.field.password')}
        </label>
        <div className="relative">
          <input
            id="save-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            required
            value={password}
            onChange={event => setPassword(event.target.value)}
            placeholder={t('auth.placeholder.createPassword')}
            aria-invalid={fieldErrors.password ? true : undefined}
            aria-describedby="save-password-hint"
            className={`${FIELD} pe-12`}
          />
          <button
            type="button"
            onClick={() => setShowPassword(current => !current)}
            aria-label={
              showPassword ? t('auth.password.hide') : t('auth.password.show')
            }
            className="absolute inset-y-0 end-0 flex min-w-[44px] items-center justify-center text-white/45 hover:text-white/70"
          >
            {showPassword ? (
              <EyeOff className="h-5 w-5" aria-hidden="true" />
            ) : (
              <Eye className="h-5 w-5" aria-hidden="true" />
            )}
          </button>
        </div>
        <p
          id="save-password-hint"
          className={`text-xs ${fieldErrors.password ? 'text-red-300' : 'text-white/60'}`}
        >
          {fieldErrors.password ?? t('guest.save.passwordHint')}
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="save-confirmation"
          className="text-sm font-medium text-white/70"
        >
          {t('auth.field.confirmPassword')}
        </label>
        <input
          id="save-confirmation"
          type={showPassword ? 'text' : 'password'}
          autoComplete="new-password"
          required
          value={confirmation}
          onChange={event => setConfirmation(event.target.value)}
          placeholder={t('auth.placeholder.confirmPassword')}
          aria-invalid={mismatch ? true : undefined}
          className={FIELD}
        />
        {mismatch ? (
          <p role="alert" className="text-sm text-red-300">
            {t('auth.validation.mismatch')}
          </p>
        ) : null}
      </div>

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="save-birth-date"
          className="text-sm font-medium text-white/70"
        >
          {t('auth.field.dateOfBirth')}
        </label>
        <input
          id="save-birth-date"
          type="date"
          required
          max={latestAdultBirthDate()}
          value={birthDate}
          onChange={event => setBirthDate(event.target.value)}
          aria-invalid={fieldErrors.birth_date ? true : undefined}
          className={FIELD}
        />
        <p
          className={`text-xs ${fieldErrors.birth_date ? 'text-red-300' : 'text-white/60'}`}
        >
          {fieldErrors.birth_date ?? t('auth.register.ageHint')}
        </p>
      </div>

      <div className="flex items-start gap-3">
        <input
          id="save-terms"
          type="checkbox"
          required
          checked={accepted}
          onChange={event => setAccepted(event.target.checked)}
          className="mt-1 h-4 w-4 rounded border-white/15 text-hc-accent focus:ring-hc-accent"
        />
        <label htmlFor="save-terms" className="text-sm text-white/70">
          {t('auth.register.consent.prefix')}{' '}
          <Link
            to="/legal/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-hc-accent-soft underline"
          >
            {t('auth.register.consent.link')}
          </Link>
          {t('auth.register.consent.and')}
          <Link
            to="/legal/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-hc-accent-soft underline"
          >
            {t('auth.register.consent.linkPrivacy')}
          </Link>
          {t('auth.register.consent.suffix')}
        </label>
      </div>

      {error ? (
        <p
          role="alert"
          className="rounded-tile border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-400"
        >
          {error}
        </p>
      ) : null}

      <div className="flex flex-col gap-2">
        <Button type="submit" size="lg" fullWidth disabled={!canSubmit}>
          {busy ? t('guest.save.busy') : t('guest.save.submit')}
        </Button>
        <Button variant="ghost" fullWidth onClick={onClose} disabled={busy}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  );
}

/**
 * "Save my account": turns the guest profile into an account in place (same
 * games, partner and answers) by adding an email, a password and a birth date.
 */
export default function SaveAccountSheet({
  open,
  onClose,
}: SaveAccountSheetProps) {
  const { t } = useTranslation();
  return (
    <Sheet open={open} onClose={onClose} title={t('guest.save.title')}>
      <SaveAccountForm onClose={onClose} />
    </Sheet>
  );
}
